import React, { useCallback, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { Button, AppView, SettingsItem, SettingsSection, NavigationHeader, SettingsItemTitle, SettingsItemContent, SettingsItemDescription, } from 'design-system';
import { IconFlag } from 'design-system/icons';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import AddressInput from 'design-system/address/AddressInput';
import useColors from 'hooks/useColors';
import { useKeyboardAdjustPan, useUserAddress } from 'hooks';
import KeyboardAvoidingView from 'design-system/KeyboardAvoidingView';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { useCommonInvestStyles } from './styles';
import { isIOS } from '../../../constants';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
const strings = {
    next: 'Continue',
    country: 'Country',
};
const countryCode = 'GB';
const country = 'United Kingdom';
const formatPostcode = (text) => text.replace(' ', '');
const InvestAddress = ({ navigation, navigation: { navigateDeprecated }, route, }) => {
    const commonStyles = useCommonInvestStyles(route.params.mountedOnAnotherScreen);
    const colors = useColors();
    const scrollY = useSharedValue(0);
    const params = route?.params || undefined;
    const mountedOnAnotherScreen = params?.mountedOnAnotherScreen;
    const addressInputRef = useRef(null);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, commonStyles.header, params?.mountedOnAnotherScreen]);
    const addressInDatabase = useRef(undefined);
    useAmplitudeOnMountEvent(params?.flow, !!mountedOnAnotherScreen, 'User Addresses Screen');
    useKeyboardAdjustPan();
    const { primaryAddress } = useUserAddress();
    useEffect(() => {
        addressInDatabase.current = primaryAddress;
    }, [primaryAddress]);
    const onSubmit = useCallback(async () => {
        const result = await addressInputRef.current?.validateInputs();
        if (!result)
            return;
        const data = {
            county: result.county,
            buildingName: result.buildingName || '',
            buildingNumber: result.buildingNumber || '',
            subBuildingName: result.subBuildingName || '',
            townOrCity: result.townOrCity,
            postcode: result.postcode,
            streetName: result.streetName,
            isPrimary: true,
            countryIsoCode: countryCode,
        };
        let skipPostAddress = false;
        let shouldUpdateAddress = false;
        // if user already had a valid UK address in database and did not change anything - skip posting address
        if (addressInDatabase.current && addressInDatabase.current.countryIsoCode === 'GB') {
            skipPostAddress = true;
            shouldUpdateAddress = true;
            if (formatPostcode(data?.postcode || '') !== formatPostcode(addressInDatabase.current.postcode || '')) {
                skipPostAddress = false;
                // shouldUpdateAddress = false; // postcode changed, which means it’s a completely different address
            }
            if (data.subBuildingName !== addressInDatabase.current.subBuildingName) {
                skipPostAddress = false;
            }
            if (data.buildingName !== addressInDatabase.current.buildingName) {
                skipPostAddress = false;
            }
            if (data.buildingNumber !== addressInDatabase.current.buildingNumber) {
                skipPostAddress = false;
            }
            if (data.streetName !== addressInDatabase.current.streetName) {
                skipPostAddress = false;
            }
            if (data.townOrCity !== addressInDatabase.current.townOrCity) {
                skipPostAddress = false;
            }
            if (data.county !== addressInDatabase.current.county) {
                skipPostAddress = false;
            }
        }
        navigateDeprecated('InvestConfirmAddress', {
            flow: params?.flow,
            postData: data,
            skipPostAddress,
            shouldUpdateAddress,
            addressInDatabase: addressInDatabase.current,
        });
    }, [params]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    return (<AppView withFrame={false}>
      <KeyboardAvoidingView style={commonStyles.keyboardAvoidingView} behavior={isIOS ? 'padding' : undefined}>
        <View style={commonStyles.containerNoPadding}>
          <StatusBar barStyle="dark-content" animated/>
          <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={params?.flow}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && commonStyles.flexEnd}>
                <HelpScreenButton />
              </View>} title={onboardingTitleStrings.Address.shortTitle}/>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={commonStyles.contentContainer}>
            <ScreenInfoHeader title={onboardingTitleStrings.Address.title} subTitle={onboardingTitleStrings.Address.subtitle}/>
            <SettingsSection noMarginTop>
              <AddressInput noExteriorView postCodeHandled ref={addressInputRef} onSubmitEditing={onSubmit} defaultAddress={primaryAddress?.countryIsoCode === 'GB' ? primaryAddress : undefined}/>
              {!!country && (<SettingsItem icon={<IconFlag />} disabled>
                  <SettingsItemContent>
                    <SettingsItemDescription>{strings.country}</SettingsItemDescription>
                    <SettingsItemTitle>{country}</SettingsItemTitle>
                  </SettingsItemContent>
                </SettingsItem>)}
            </SettingsSection>
          </Animated.ScrollView>
        </View>
      </KeyboardAvoidingView>
      <Button brand onPress={onSubmit} title={strings.next} style={[mountedOnAnotherScreen ? commonStyles.mountedButton : commonStyles.button]}/>
    </AppView>);
};
export default InvestAddress;
